// Constants / utils
import { convertModulesToSaveState } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/utils/custom-pdf-report-builder-utils';
import { EFFICIENT_FRONTIER_MODULE_TYPES, FIELD_TYPES_WITH_RELATED_ITEMS, MODULE_TYPES_WITH_RELATED_ITEMS, PAGE_SIZE_MAX, POSSIBLE_MODULE_SIZES, REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES, TargetSecurityChoice, } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/constants';
import { getSectionsWithModules, getSortedSections, hasValidReportSecurities, } from 'CustomPDFReportsApp/ts/utils/custom-pdf-report-utils';
// NgRx Selectors
import { createDeepEqualSelector } from 'NewAngular/state/selectors';
const getReportType = (state) => (state && state.report.report_type) || null;
const getReportOrientation = (state) => (state && state.report.report_orientation) || null;
const getReportName = (state) => (state && state.report.name) || '';
const getReportDescription = (state) => (state && state.report.description) || '';
const getReportFooter = (state) => (state && state.report.report_footer) || null;
const getReportModules = (state) => (state && state.report.modules) || [];
const getDataSource = (state) => (state && state.report.data_source) || '';
const getPerformanceDisclosureFootnote = (state) => (state && state.report.performance_disclosure_footnote) || '';
const getSections = (state) => {
    const modules = (state && state.report.modules) || [];
    let sections = (state && state.report.sections) || [];
    sections = getSectionsWithModules(sections, modules);
    return getSortedSections(sections);
};
const getRemoveReportTitleHeader = (state) => (state && state.report.remove_report_title_header) || false;
const getIncludeBenchmarkDefault = (state) => (state && state.report.include_benchmark_default) || false;
export const getAllowedModuleSizesByPage = createDeepEqualSelector(getReportModules, (modules) => {
    const allowedModuleSizesByPage = {};
    modules.forEach((page, pageNum) => {
        const remainingSpace = PAGE_SIZE_MAX -
            page.reduce((acc, module) => {
                return acc + module.module_size;
            }, 0);
        allowedModuleSizesByPage[pageNum] = POSSIBLE_MODULE_SIZES.filter((size) => size <= remainingSpace);
    });
    return allowedModuleSizesByPage;
});
export const hasEfficientFrontierModules = createDeepEqualSelector(getReportModules, (modules) => {
    return modules.some((page, _) => page.some((module) => EFFICIENT_FRONTIER_MODULE_TYPES.includes(module.module_type)));
});
const getAdvancedOptions = (state) => state.report.advanced_options;
export const getCustomPDFReportSaveParams = createDeepEqualSelector(getReportType, getReportOrientation, getReportName, getReportDescription, getReportFooter, getReportModules, getIncludeBenchmarkDefault, getAdvancedOptions, getDataSource, getPerformanceDisclosureFootnote, getSections, getRemoveReportTitleHeader, (report_type, report_orientation, name, description, report_footer, modules, include_benchmark_default, advanced_options, data_source, performance_disclosure_footnote, sections, remove_report_title_header) => {
    return {
        report_type,
        report_orientation,
        name,
        description,
        report_footer,
        modules: convertModulesToSaveState(modules),
        include_benchmark_default,
        advanced_options,
        data_source,
        performance_disclosure_footnote,
        sections,
        remove_report_title_header,
    };
});
export const canSave = createDeepEqualSelector(getReportType, getReportModules, (reportType, modules) => {
    // Must have selected a report type and have at least one module on the report in order to save it
    return !!reportType && modules.some((page) => page.length);
});
export const getWarningMessage = createDeepEqualSelector(getReportModules, (modules) => {
    const pagesMissingInput = [];
    modules.forEach((page, pageNum) => {
        page.forEach((module) => {
            if (MODULE_TYPES_WITH_RELATED_ITEMS.has(module.module_type) &&
                !Object.values(module.customizable_fields).some((field) => FIELD_TYPES_WITH_RELATED_ITEMS.has(field.type) && field.value)) {
                pagesMissingInput.push(pageNum + 1);
            }
        });
    });
    let warningMessage = '';
    if (pagesMissingInput.length) {
        if (pagesMissingInput.length === 1) {
            warningMessage = `Page ${pagesMissingInput[0]} has a module with no attachment/security. This page will be blank when the report is generated.`;
        }
        else if (pagesMissingInput.length === 2) {
            warningMessage = `Pages ${pagesMissingInput[0]} and ${pagesMissingInput[1]} have a module with no attachment/security. These pages will be blank when the report is generated.`;
        }
        else {
            warningMessage = `Pages ${pagesMissingInput.slice(0, pagesMissingInput.length - 1).join(', ')}, and ${pagesMissingInput[pagesMissingInput.length - 1]} have a module with no attachment/security. These pages will be blank when the report is generated.`;
        }
    }
    return warningMessage;
});
const getPreviewSecurities = (state) => state.preview_securities;
export const getNumRequiredSecurities = (state) => {
    let maxNumRequiredSecurities = 0;
    if (state.report?.report_type && state.report?.report_type in REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES) {
        maxNumRequiredSecurities =
            REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES[state.report.report_type];
        if (state.report.modules) {
            state.report.modules.flat().forEach((module) => {
                let numRequiredSecurities = 1;
                const targetSecurityChoice = module.customizable_fields?.target_security_choice?.value;
                if (targetSecurityChoice === TargetSecurityChoice.SECONDARY_SECURITY) {
                    numRequiredSecurities = 2;
                }
                else if (module.show_num_securities_field) {
                    numRequiredSecurities =
                        REPORT_TYPE_TO_NUM_REQUIRED_SECURITIES[module.report_type];
                }
                if (numRequiredSecurities > maxNumRequiredSecurities) {
                    maxNumRequiredSecurities = numRequiredSecurities;
                }
            });
        }
    }
    return maxNumRequiredSecurities;
};
export const hasValidPreviewSecurities = createDeepEqualSelector(getReportType, getPreviewSecurities, (reportType, previewSecurities) => {
    return hasValidReportSecurities(reportType, previewSecurities.primary, previewSecurities.secondary, previewSecurities.comparison);
});
export const getActiveTabIndex = (tabs) => {
    return tabs.findIndex((tab) => tab.isActive);
};
export const getIsEdited = (state) => {
    const activeTabIndex = getActiveTabIndex(state.tabs);
    if (activeTabIndex === -1) {
        return false;
    }
    return state.tabs[activeTabIndex].isEdited;
};
export const getActiveTab = (tabs) => {
    const activeTabIndex = getActiveTabIndex(tabs);
    if (activeTabIndex === -1) {
        return null;
    }
    return tabs[activeTabIndex];
};
